import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'GolfBoard Scores';
const BASE_URL  = 'https://golfboardscores.com';
const OG_IMAGE  = `${BASE_URL}/og-image.png`;

/**
 * SEO component — drop into any page to set title, description, OG tags,
 * and optional JSON-LD structured data for that route.
 *
 * Usage:
 *   <SEO
 *     title="Pebble Beach Leaderboard"
 *     description="See how you rank on the Pebble Beach Golf Links leaderboard."
 *     canonical="/public/leaderboard/abc123"
 *   />
 */
export function SEO({
  title,
  description,
  canonical,
  image = OG_IMAGE,
  noIndex = false,
  jsonLd = null,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Amateur Golf Leaderboards & Score Tracker`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : BASE_URL;

  return (
    <Helmet>
      {/* ── Primary ── */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* ── Open Graph ── */}
      <meta property="og:title"       content={fullTitle} />
      <meta property="og:description" content={description || ''} />
      <meta property="og:url"         content={canonicalUrl} />
      <meta property="og:image"       content={image} />
      <meta property="og:type"        content="website" />
      <meta property="og:site_name"   content={SITE_NAME} />

      {/* ── Twitter / X ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:title"       content={fullTitle} />
      <meta name="twitter:description" content={description || ''} />
      <meta name="twitter:image"       content={image} />

      {/* ── JSON-LD (optional) ── */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
}

// ── Pre-built SEO configs for every page ─────────────────────────────────────

export function LandingPageSEO() {
  return (
    <SEO
      title={null} // Uses the full default title from index.html
      description="Track your golf scores and compete on leaderboards at any course in the world. Free amateur golf score tracker with city, state, and country rankings. No tournament required."
      canonical="/"
      jsonLd={{
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'GolfBoard Scores',
        url: 'https://golfboardscores.com',
        description: 'Free amateur golf leaderboard and score tracking platform.',
      }}
    />
  );
}

export function LoginPageSEO() {
  return (
    <SEO
      title="Sign In or Create Account"
      description="Sign in to GolfBoard Scores to post golf scores, track your handicap, and compete on amateur leaderboards at courses worldwide."
      canonical="/login"
    />
  );
}

export function TermsPageSEO() {
  return (
    <SEO
      title="Terms & Conditions"
      description="Terms and conditions for using GolfBoard Scores, the free amateur golf score tracking and leaderboard platform."
      canonical="/terms"
    />
  );
}

export function PrivacyPageSEO() {
  return (
    <SEO
      title="Privacy Policy"
      description="Privacy policy for GolfBoard Scores. Learn how we collect, use, and protect your information."
      canonical="/privacy"
    />
  );
}

export function ContactPageSEO() {
  return (
    <SEO
      title="Contact Us"
      description="Get in touch with the GolfBoard Scores team. Questions, feedback, or bug reports — we'd love to hear from you."
      canonical="/contact"
    />
  );
}

export function DonatePageSEO() {
  return (
    <SEO
      title="Support GolfBoard Scores"
      description="GolfBoard Scores is a free passion project. If it's brought value to your game, consider buying us a coffee to keep it running."
      canonical="/donate"
    />
  );
}

/**
 * Public leaderboard page — receives dynamic course/tee info.
 * This is the most SEO-valuable dynamic page since it's publicly accessible.
 */
export function PublicLeaderboardSEO({ courseName, teeColor, city, state, country }) {
  const location = [city, state, country].filter(Boolean).join(', ');
  const title = courseName
    ? `${courseName}${teeColor ? ` (${teeColor} Tees)` : ''} Leaderboard`
    : 'Golf Course Leaderboard';
  const description = courseName
    ? `See the top amateur golf scores on the ${teeColor ? `${teeColor} tees at ` : ''}${courseName}${location ? ` in ${location}` : ''}. Free leaderboard on GolfBoard Scores.`
    : 'View amateur golf scores and leaderboard rankings on GolfBoard Scores.';

  const jsonLd = courseName ? {
    '@context': 'https://schema.org',
    '@type': 'SportsEvent',
    'name': `${courseName} Amateur Leaderboard`,
    'description': description,
    'location': {
      '@type': 'SportsActivityLocation',
      'name': courseName,
      'address': {
        '@type': 'PostalAddress',
        'addressLocality': city || '',
        'addressRegion': state || '',
        'addressCountry': country || '',
      },
    },
    'organizer': {
      '@type': 'Organization',
      'name': 'GolfBoard Scores',
      'url': 'https://golfboardscores.com',
    },
  } : null;

  return (
    <SEO
      title={title}
      description={description}
      jsonLd={jsonLd}
    />
  );
}

// Authenticated pages — these should never be indexed
export function AuthenticatedPageSEO({ title }) {
  return <SEO title={title} noIndex={true} />;
}